<template>
	<div class="results-toolbar results-toolbar--vendors-history">
		<div class="results-toolbar__search">
			<Search
				:placeholder="placeHolder"
				v-model="filters.search"
				:timeout="300"
				@change="emitValues"
			/>
		</div>
		<Button
			v-if="notDefaultFilters"
			class="results-toolbar--vendors-history__reset-filters"
			className="btn--link"
			labelText="Restore Filters"
			:btnClick="resetFilters"
		/>
		<div class="results-toolbar--vendors-history__desktop" ref="desktopRef">
			<DatePicker
				v-model="filters.dateRange"
				@change="emitValues"
				required
			/>
			<DropdownV2
				v-model="filters.changedByUserId"
				:options="userOptions"
				placeholder="Select User"
				@change="emitValues"
			/>
		</div>
		<div class="results-toolbar--vendors-history__mobile" ref="mobileRef">
			<Button
				className="btn--primary"
				iconClassName="icon-filter"
				:iconBeforeLabel="true"
				:btnClick="() => (displayFiltersModal = true)"
			/>
		</div>
		<Modal
			v-if="displayFiltersModal"
			:display="true"
			title="Apply Filters"
			@show="displayFiltersModal = false"
		>
			<template #modal-content>
				<DatePicker v-model="filtersModal.dateRange" />
				<DropdownV2
					v-model="filtersModal.changedByUserId"
					:options="userOptions"
					placeholder="Select User"
				/>
			</template>
			<template #modal-footer>
				<ButtonGroup>
					<template #container>
						<Button
							labelText="Apply Filters"
							className="btn--primary"
							:btnClick="applyFilterUsingModal"
							:disabled="!filtersModalHaveChanges"
						/>
						<Button
							labelText="Close"
							className="btn--link"
							:btnClick="closeModalFilters"
						/>
					</template>
				</ButtonGroup>
			</template>
		</Modal>
	</div>
</template>

<script>
import Search from '@/components/forms/search/search';
import DatePicker from '@/components/forms/date-picker/date-picker';
import ButtonGroup from '@/components/forms/button/button-group';
import Button from '@/components/forms/button/button';
import DropdownV2 from '@/components/forms/dropdown/dropdown-v2.vue';
import Modal from '@/components/shells/modal/modals';

// libraries
import { formatDate } from '@/utils/datetime';
import { sub, isValid, parse } from 'date-fns';
import { deepEqualNoFn } from '@/utils/object';
import _ from 'lodash';

export default {
	name: 'VendorsHistoryFilters',
	components: {
		Search,
		DatePicker,
		ButtonGroup,
		Button,
		DropdownV2,
		Modal
	},
	props: {
		value: {
			type: Object,
			default: () => {}
		},
		defaultFilters: Object,
		counter: String,
		userOptions: Array,
		placeHolder: {
			type: String,
			default: 'Search by form name or description'
		}
	},
	data() {
		return {
			displayFiltersModal: false,
			filters: {},
			filtersModal: {},
			enabledOptions: [
				{ text: 'All Statuses', value: 'all' },
				{ text: 'Enabled', value: true },
				{ text: 'Disabled', value: false }
			]
		};
	},
	created() {
		this.filters = _.cloneDeep(this.value);
		this.filtersModal = _.cloneDeep(this.value);

		// format the date
		this.createDateValueForDatePicker(this.filters);
		this.createDateValueForDatePicker(this.defaultFilters);
	},
	computed: {
		filtersModalHaveChanges() {
			return !deepEqualNoFn(this.filters, this.filtersModal, true);
		},
		notDefaultFilters() {
			// remove search from default
			const defaultFiltersLocal = _.cloneDeep(this.defaultFilters);
			delete defaultFiltersLocal.search;
			delete defaultFiltersLocal.dateRange.startDate;
			delete defaultFiltersLocal.dateRange.endDate;

			const filtersLocal = _.cloneDeep(this.filters);
			delete filtersLocal.search;
			delete filtersLocal.dateRange.startDate;
			delete filtersLocal.dateRange.endDate;

			return !deepEqualNoFn(defaultFiltersLocal, filtersLocal);
		},
		filtersToEmit() {
			const filtersToEmit = { ...this.filters };

			filtersToEmit.dateRange.minDatetime =
				formatDate(this.filters.dateRange.startDate) || undefined;
			filtersToEmit.dateRange.maxDatetime =
				formatDate(this.filters.dateRange.endDate) || undefined;

			return filtersToEmit;
		}
	},
	methods: {
		applyFilterUsingModal() {
			this.filters = _.cloneDeep(this.filtersModal);
			this.displayFiltersModal = false;
			this.$emit('input', { ...this.filtersToEmit });
		},
		resetFilters() {
			this.filters = _.cloneDeep(this.defaultFilters);
			this.filtersModal = _.cloneDeep(this.defaultFilters);
			this.$emit('input', { ...this.filtersToEmit });
		},
		closeModalFilters() {
			this.filtersModal = _.cloneDeep(this.filters);
			this.displayFiltersModal = false;
		},
		emitValues() {
			this.filtersModal = _.cloneDeep(this.filters);

			this.$emit('input', { ...this.filtersToEmit });
		},
		createDateValueForDatePicker(obj) {
			const minDate = parse(
				obj.dateRange.minDatetime,
				'MM-dd-yyyy',
				new Date()
			);
			const maxDate = parse(
				obj.dateRange.maxDatetime,
				'MM-dd-yyyy',
				new Date()
			);

			if (isValid(minDate)) {
				obj.dateRange.startDate = formatDate(
					new Date(obj.dateRange.minDatetime)
				);
			} else {
				obj.dateRange.startDate = formatDate(
					new Date(sub(new Date(), { weeks: 1 }))
				);
			}

			if (isValid(maxDate)) {
				obj.dateRange.endDate = formatDate(
					new Date(obj.dateRange.maxDatetime)
				);
			} else {
				obj.dateRange.endDate = formatDate(new Date());
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.results-toolbar--vendors-history {
	width: 100%;

	&__reset-filters {
		margin-left: auto;
	}

	&__mobile {
		margin-left: $gutter-lg;

		@include mq($from: md) {
			margin-left: 0;
		}

		@include mq($from: md-l) {
			display: none;
		}
	}

	&__desktop {
		display: none;
		z-index: 100;

		@include mq($from: md-l) {
			display: flex;
			gap: $gutter-lg;
		}
	}
}
</style>
